import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import { useAuth } from "oidc-react";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DatePicker, { registerLocale } from "react-datepicker";
import { useLanguage } from "../components/LanguageProvider";
import { enGB, sv } from "date-fns/locale";
import { CalendarMonth, Check, WarningAmber } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import {
  addDays,
  startOfWeek,
  format,
  isToday,
  getISOWeek,
  getYear,
} from "date-fns";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/NavigateBefore";
import ArrowForwardIcon from "@mui/icons-material/NavigateNext";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import "../components/Agenda.css";
import "react-datepicker/dist/react-datepicker.css";
import ReportTimeModal from "../components/TimeReport/ReportTimeModal";
import TimeReportItem, {
  STATUS_COLORS,
} from "../components/TimeReport/TimeReportItem";
import EditTimeDialog from "../components/TimeReport/EditTimeDialog";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  List,
  ListItem,
  ListItemText,
  Box,
} from "@mui/material";
import { useToastState } from "../components/ToastStateProvider";
import Loading from "../components/Loading";
import useMediaQuery from "@mui/material/useMediaQuery";
import SubMaterialModal from "../components/SubMaterialModal";

const roundToTwoDecimals = (value) => {
  return Number(Math.round(value + "e2") + "e-2");
};

// Wage code types to exclude from daily reported hours calculations
const EXCLUDED_WAGE_CODE_TYPES = [
  "Information",
  "Overtime",
  "Extra time",
  "Increment",
  "ProjectCost",
];

const TimeReport = () => {
  const [reportedTimes, setReportedTimes] = useState([]);
  const [checkedDates, setCheckedDates] = useState({});
  const [scheduledHours, setScheduledHours] = useState([]);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [expandedDays, setExpandedDays] = useState([]);
  const [editTimeDialogOpen, setEditTimeDialogOpen] = useState(false);
  const [timeItemToEdit, setTimeItemToEdit] = useState(null);

  const { t, i18n } = useTranslation();
  const { language, dateFnsLocale } = useLanguage();
  const auth = useAuth();
  const accessToken = auth.userData?.access_token;
  const rootUrl = import.meta.env.VITE_BACKEND_API_ROOT_URL;
  const refDatePicker = useRef(null);
  const latestPrefillRequestId = useRef(null);
  const isMountedRef = useRef(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [holidays, setHolidays] = useState(null);
  const [holidaysDatePicker, setHolidaysDatePicker] = useState(null);
  const [open, setOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [pendingPrefillId, setPendingPrefillId] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const today = new Date();
  let year = getYear(new Date());
  const { setToastState } = useToastState();
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const location = useLocation();
  const navigate = useNavigate();
  const [subMaterialOpen, setSubMaterialOpen] = useState(false);
  const [subMaterialWoNo, setSubMaterialWoNo] = useState(null);
  const [subMaterialLoading, setSubMaterialLoading] = useState(false);
  const [subMaterialDate, setSubMaterialDate] = useState(null);
  const [subMaterialItem, setSubMaterialItem] = useState(null);

  const [subMaterialCounter] = useState(0);
  const noopSetCounter = () => {};

  registerLocale(language, dateFnsLocale);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const planningIdFromQuery = searchParams.get("prefillPlanningId");
    const planningIdFromState =
      location.state?.prefillPlanningId != null
        ? String(location.state.prefillPlanningId)
        : null;
    const planningId = planningIdFromQuery ?? planningIdFromState;

    if (planningId) {
      setPendingPrefillId((prev) => (prev === planningId ? prev : planningId));
    }
  }, [location.search, location.state]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const showToast = useCallback(
    (message, severity) => {
      setToastState({ message, severity, open: true });
    },
    [setToastState]
  );

  const handleAddFromItem = async (item) => {
    if (item.type === "Workorder") {
      const wono = item.workOrderNumber;
      const selDate = item.date;

      setSubMaterialDate(selDate);
      setSubMaterialLoading(true);
      setSubMaterialWoNo(wono);
      setSubMaterialItem(item);

      setSubMaterialLoading(false);
      setSubMaterialOpen(true);
      return;
    }

    if (item.type === "Project") {
      if (!item.projectId || !item.subProjectId || !item.projectActivityNo) {
        showToast(
          t("timeReport.addTimeDialog.errors.informationMissing"),
          "warning"
        );
        return;
      }

      if (item.date) {
        const parsedDate = new Date(item.date);
        if (!Number.isNaN(parsedDate.getTime())) {
          setSelectedDate(parsedDate);
        }
      }

      setPendingPrefillId(null);
      setPrefillData({
        type: "Project",
        projectId: item.projectId,
        subProjectId: item.subProjectId,
        projectActivityNo: item.projectActivityNo,
        date: item.date,
        hours: item.hours ?? 0,
      });
      setEditItem(null);
      setModalOpen(true);
    }
  };

  const handleCloseSubMaterial = () => {
    setSubMaterialOpen(false);
    setSubMaterialWoNo(null);
    setSubMaterialItem(null);
  };

  const handleSubMaterialSuccess = useCallback(
    (payload) => {
      if (!subMaterialItem) {
        return;
      }

      const nextDate =
        payload?.date != null ? payload.date : subMaterialItem.date;
      const nextHours =
        payload?.quantity != null ? payload.quantity : subMaterialItem.hours;

      setReportedTimes((prevTimes) =>
        prevTimes.map((time) => {
          if (time.type !== "Workorder") {
            return time;
          }

          const matchesId =
            subMaterialItem.id != null && time.id === subMaterialItem.id;
          const matchesComposite =
            !matchesId &&
            time.workOrderNumber === subMaterialItem.workOrderNumber &&
            time.rowNo === subMaterialItem.rowNo &&
            String(time.date ?? "") === String(subMaterialItem.date ?? "");

          if (matchesId || matchesComposite) {
            return {
              ...time,
              hours: nextHours,
              date: nextDate ?? time.date,
            };
          }

          return time;
        })
      );
    },
    [subMaterialItem]
  );

  useEffect(() => {
    if (!pendingPrefillId || !accessToken) {
      return;
    }

    const currentRequestId = pendingPrefillId;
    latestPrefillRequestId.current = currentRequestId;
    const abortController = new AbortController();

    const fetchPrefillData = async () => {
      if (isMountedRef.current) {
        setPrefillLoading(true);
      }
      try {
        const response = await fetch(
          `${rootUrl}timereport/project/prefillinfo?projectPlanningId=${currentRequestId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            signal: abortController.signal,
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch prefill data");
        }

        const data = await response.json();

        if (
          !isMountedRef.current ||
          abortController.signal.aborted ||
          latestPrefillRequestId.current !== currentRequestId
        ) {
          return;
        }

        if (data?.date) {
          const parsedDate = new Date(data.date);
          if (!Number.isNaN(parsedDate.getTime())) {
            setSelectedDate(parsedDate);
          }
        }

        setPrefillData(data);
        setModalOpen(true);
        setEditItem(null);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching prefill info:", error);
        if (
          isMountedRef.current &&
          !abortController.signal.aborted &&
          latestPrefillRequestId.current === currentRequestId
        ) {
          showToast(t("timeReport.prefill.loadError"), "error");
        }
      } finally {
        if (
          !isMountedRef.current ||
          abortController.signal.aborted ||
          latestPrefillRequestId.current !== currentRequestId
        ) {
          return;
        }

        setPrefillLoading(false);
        setPendingPrefillId(null);
        navigate(location.pathname, { replace: true, state: {} });
        latestPrefillRequestId.current = null;
      }
    };

    fetchPrefillData();

    return () => {
      abortController.abort();
    };
  }, [
    pendingPrefillId,
    accessToken,
    rootUrl,
    navigate,
    location.pathname,
    showToast,
  ]);
  const getReportedTimes = async () => {
    const startDate = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const endDate = addDays(startDate, 6);
    const startDateString = format(startDate, "yyyy-MM-dd");
    const endDateString = format(endDate, "yyyy-MM-dd");

    try {
      const response = await fetch(
        `${rootUrl}timereport/reported?startDate=${startDateString}&endDate=${endDateString}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch data");
      }

      const data = await response.json();
      setReportedTimes(data);

      await getScheduledHours(startDateString, endDateString);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const getScheduledHours = async (startDateString, endDateString) => {
    try {
      const response = await fetch(
        `${rootUrl}timereport/schedule?startDate=${startDateString}&endDate=${endDateString}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch scheduled hours");
      }

      const result = await response.json();
      setScheduledHours(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      console.error("Error fetching scheduled hours:", error);
    }
  };

  useEffect(() => {
    getReportedTimes();
  }, [selectedDate]);

  useEffect(() => {
    const fetchHolidays = async () => {
      try {
        if (year !== getYear(selectedDate) || !holidays) {
          year = getYear(selectedDate);
        } else {
          return;
        }
        let url = `${rootUrl}Calendar/holidays/${year}`;
        const response = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setHolidays(data);
        }
      } catch (error) {
        console.error("Error fetching holiday value:", error);
      }
    };
    fetchHolidays();
  }, [selectedDate]);

  useEffect(() => {
    if (holidays) {
      const updatedHolidays = holidays.map((holiday) => ({
        date: holiday.date.substring(0, 10),
        holidayName: i18n.language === "sv" ? holiday.localName : holiday.name,
      }));
      setHolidaysDatePicker(updatedHolidays);
    }
  }, [holidays, i18n.language]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpen(false);
  };

  const moveByDays = (dateAmount) => {
    const newSelectedDate = addDays(selectedDate, dateAmount);
    setSelectedDate(newSelectedDate);
  };

  const goToPreviousWeek = () => {
    moveByDays(-7);
  };

  const goToNextWeek = () => {
    moveByDays(7);
  };
  useEffect(() => {
    if (accessToken) {
      getReportedTimes();
    }
  }, [accessToken]);

  useEffect(() => {
    if (reportedTimes.length > 0) {
      const newCheckedDates = {};
      reportedTimes.forEach((time) => {
        if (time.status === "Unconfirmed") {
          newCheckedDates[time.date] = true;
        }
      });
      setCheckedDates(newCheckedDates);
    }
  }, [reportedTimes]);
  const handleAddTimeClick = () => {
    setModalOpen(true);
  };
  const handleCheckboxChange = (date, checked) => {
    setCheckedDates((prev) => ({
      ...prev,
      [date]: checked,
    }));
  };
  const handleConfirmClick = () => {
    const datesToConfirm = Object.entries(checkedDates)
      .filter(([_, isChecked]) => isChecked)
      .map(([date]) => date);

    if (datesToConfirm.length === 0) return;

    setConfirmDialogOpen(true);
  };

  const confirmSelectedDates = async () => {
    const datesToConfirm = Object.entries(checkedDates)
      .filter(([_, isChecked]) => isChecked)
      .map(([date]) => date);

    if (datesToConfirm.length === 0) return;

    setConfirmLoading(true);
    setConfirmDialogOpen(false);

    try {
      const response = await fetch(`${rootUrl}timereport/confirmdates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(datesToConfirm),
      });
      if (!response.ok) {
        showToast(t("timeReport.confirmDates.errorMessage"), "error");
      } else {
        showToast(t("timeReport.confirmDates.successMessage"), "success");

        // Refresh reported times after confirmation
        await getReportedTimes();
      }
    } catch (error) {
      showToast(t("timeReport.confirmDates.errorMessage"), "error");
      console.error("Error confirming dates:", error);
    } finally {
      setConfirmLoading(false);
    }
  };
  const handleEditTimeReport = (item) => {
    if (item.type === "Workorder") {
      setTimeItemToEdit(item);
      setEditTimeDialogOpen(true);
    } else {
      setEditItem(item);
      setModalOpen(true);
    }
  };

  const handleDeleteTimeReport = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTimeReport = async () => {
    if (!itemToDelete) return;

    try {
      if (itemToDelete.type === "Workorder") {
        // For workorder items, set hours to 0 and call update endpoint
        const response = await fetch(
          `${rootUrl}timereport/workorder/${itemToDelete.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              woNo: itemToDelete.workOrderNumber,
              hours: 0,
              rowNo: itemToDelete.rowNo,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete workorder time report");
        }
      } else {
        // For non-workorder items, use the original DELETE endpoint
        const response = await fetch(
          `${rootUrl}timereport/${itemToDelete.id}?type=${itemToDelete.type}`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete time report");
        }
      }

      showToast(t("timeReport.deleteSuccess"), "success");
      await getReportedTimes();
    } catch (error) {
      console.error("Error deleting time report:", error);
      showToast(t("timeReport.deleteError"), "error");
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleCloseModal = (event, reason) => {
    if (reason !== "backdropClick") {
      setModalOpen(false);
      setEditItem(null);
      setPrefillData(null);
      setPendingPrefillId(null);
    }
  };

  const CustomInput = React.forwardRef(({ value }, ref) => (
    <div className="datepicker-input-container" ref={ref}>
      <IconButton
        onClick={() => {
          setOpen(!open);
        }}
      >
        <CalendarMonth />
      </IconButton>
    </div>
  ));
  const datePickerContainerRef = useRef(null);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#2196f3",
      },
    },
  });

  const handleSave = async (submittedTimeReports = [], isEdit) => {
    if (isEdit) {
      await getReportedTimes();
      return;
    }

    setReportedTimes((prevTimes) => {
      const nextTimes = [...prevTimes];

      submittedTimeReports.forEach((item) => {
        if (!item) {
          return;
        }

        if (item.type === "Project") {
          const hasId = item.id != null;
          let existingIndex = -1;

          if (hasId) {
            existingIndex = nextTimes.findIndex(
              (time) => time.id != null && time.id === item.id
            );
          }

          if (existingIndex === -1) {
            existingIndex = nextTimes.findIndex(
              (time) =>
                time.type === "Project" &&
                String(time.projectId ?? "") === String(item.projectId ?? "") &&
                String(time.subProjectId ?? "") ===
                  String(item.subProjectId ?? "") &&
                String(time.projectActivityNo ?? "") ===
                  String(item.projectActivityNo ?? "") &&
                String(time.date ?? "") === String(item.date ?? "")
            );
          }

          if (existingIndex >= 0) {
            nextTimes[existingIndex] = item;
          } else {
            nextTimes.push(item);
          }
          return;
        }

        nextTimes.push(item);
      });

      return nextTimes;
    });
  };

  // Reported hours per date (excluding specific wage code types)
  const reportedHoursByDate = useMemo(() => {
    // Exclude items where name contains (mil), mil), or (st), case-insensitive
    const EXCLUDED_PATTERNS = [/\(mil\)/i, /mil\)/i, /\(st\)/i];
    return reportedTimes.reduce((acc, time) => {
      const isProject = time.type === "Project";
      const targetStr = isProject ? time.description || "" : time.name || "";
      const hasExcluded = EXCLUDED_PATTERNS.some((pattern) =>
        pattern.test(targetStr)
      );
      if (
        !EXCLUDED_WAGE_CODE_TYPES.includes(time.wageCodeType) &&
        !hasExcluded
      ) {
        const dateKey = time.date;
        const hours = parseFloat(time.hours || 0);
        const safeHours = Number.isNaN(hours) ? 0 : hours;
        acc[dateKey] = (acc[dateKey] || 0) + safeHours;
      }
      return acc;
    }, {});
  }, [reportedTimes]);

  const getReportedHoursForDate = (dateStr) =>
    reportedHoursByDate[dateStr] || 0;

  const workOrderGroupTitleByNumber = useMemo(() => {
    const groupTitleMap = new Map();

    reportedTimes.forEach((item) => {
      const workOrderNumber = String(item.workOrderNumber ?? "").trim();
      const wageCode = String(item.wageCode ?? "").trim();
      const name = String(item.name ?? "").trim();

      if (!workOrderNumber || wageCode !== "" || !name) {
        return;
      }

      if (!groupTitleMap.has(workOrderNumber)) {
        groupTitleMap.set(workOrderNumber, `${workOrderNumber} - ${name}`);
      }
    });

    return groupTitleMap;
  }, [reportedTimes]);

  const holidayByDate = useMemo(() => {
    return new Map(
      (holidaysDatePicker ?? []).map((holiday) => [
        holiday.date,
        holiday.holidayName,
      ])
    );
  }, [holidaysDatePicker]);

  const buildDayDisplayEntries = useCallback(
    (dayReports) => {
      const entries = [];
      const groupByWorkOrder = new Map();
      const groupByProject = new Map();

      dayReports.forEach((item) => {
        const workOrderNumber = String(item.workOrderNumber ?? "").trim();
        const isGroupedWorkOrderItem =
          item.type === "Workorder" && workOrderNumber !== "";

        if (isGroupedWorkOrderItem) {
          let group = groupByWorkOrder.get(workOrderNumber);

          if (!group) {
            group = {
              type: "workorder-group",
              workOrderNumber,
              title:
                workOrderGroupTitleByNumber.get(workOrderNumber) ||
                `AO - ${workOrderNumber}`,
              items: [],
            };

            groupByWorkOrder.set(workOrderNumber, group);
            entries.push(group);
          }

          group.items.push(item);
          return;
        }

        const projectId = String(item.projectId ?? "").trim();
        const isGroupedProjectItem =
          item.type === "Project" && projectId !== "";

        if (isGroupedProjectItem) {
          let group = groupByProject.get(projectId);

          if (!group) {
            const projectName = String(
              item.projectName ?? item.name ?? item.projShortName ?? ""
            ).trim();
            const titleSuffix = projectName ? ` - ${projectName}` : "";
            group = {
              type: "project-group",
              projectId,
              title: `${projectId}${titleSuffix}`,
              items: [],
            };

            groupByProject.set(projectId, group);
            entries.push(group);
          }

          group.items.push(item);
          return;
        }

        entries.push({ type: "item", item });
      });

      return entries;
    },
    [workOrderGroupTitleByNumber]
  );

  const getStatusColorForItems = useCallback((items) => {
    if (!items || items.length === 0) {
      return "white";
    }

    const hasUnconfirmed = items.some((item) => item.status === "Unconfirmed");
    const allApproved = items.every((item) => item.status === "Approved");
    const allConfirmed = items.every((item) => item.status === "Confirmed");

    if (hasUnconfirmed) {
      return STATUS_COLORS.Unconfirmed;
    }
    if (allApproved) {
      return STATUS_COLORS.Approved;
    }
    if (allConfirmed) {
      return STATUS_COLORS.Confirmed;
    }

    return "white";
  }, []);

  return (
    <div
      style={{ height: "100vh", overflow: "hidden", backgroundColor: "#eee" }}
    >
      <Header />
      <ThemeProvider theme={theme}>
        <Container>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            style={{ height: "7vh", margin: "0", padding: "0" }}
          >
            <Grid item>
              <Grid container direction="row">
                <Grid item>
                  <div ref={datePickerContainerRef}>
                    <DatePicker
                      ref={refDatePicker}
                      onChange={handleDateChange}
                      selected={selectedDate}
                      dateFormat="yyyy-MM-dd"
                      locale={language && language.startsWith("en") ? enGB : sv}
                      customInput={<CustomInput />}
                      open={open}
                      popperPlacement="bottom-start"
                      showWeekNumbers
                      holidays={holidaysDatePicker}
                    />
                  </div>
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <Typography>
                {t("week")}&nbsp;{getISOWeek(selectedDate)}
              </Typography>
            </Grid>
            <Grid item>
              <Grid container direction="row" alignItems="center">
                <IconButton onClick={goToPreviousWeek} sx={{ padding: "6px" }}>
                  <ArrowBackIcon />
                </IconButton>

                <Typography
                  variant="body1"
                  style={{ margin: "0", cursor: "pointer" }}
                  onClick={() => {
                    setSelectedDate(today);
                  }}
                >
                  {t("today")}
                </Typography>

                <IconButton onClick={goToNextWeek} sx={{ padding: "6px" }}>
                  <ArrowForwardIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
          <div
            style={{
              marginTop: "20px",
              overflowY: "auto",
              maxHeight: "65dvh",
            }}
          >
            {[...Array(7)].map((_, index) => {
              const currentDay = addDays(
                startOfWeek(selectedDate, { weekStartsOn: 1 }),
                index
              );
              const dayReports = reportedTimes.filter(
                (time) => time.date === format(currentDay, "yyyy-MM-dd")
              );
              const dayDisplayEntries = buildDayDisplayEntries(dayReports);

              let statusColor = "white";
              let hasUnconfirmed = false;
              if (dayReports.length > 0) {
                hasUnconfirmed = dayReports.some(
                  (item) => item.status === "Unconfirmed"
                );
                const allApproved = dayReports.every(
                  (item) => item.status === "Approved"
                );
                const allConfirmed = dayReports.every(
                  (item) => item.status === "Confirmed"
                );

                if (hasUnconfirmed) {
                  statusColor = STATUS_COLORS.Unconfirmed;
                } else if (allApproved) {
                  statusColor = STATUS_COLORS.Approved;
                } else if (allConfirmed) {
                  statusColor = STATUS_COLORS.Confirmed;
                }
              }

              const isExpanded = expandedDays.includes(index);
              const currentDayKey = format(currentDay, "yyyy-MM-dd");
              const currentHolidayName = holidayByDate.get(currentDayKey);
              const summaryBaseBackground = currentHolidayName
                ? "rgba(211, 47, 47, 0.12)"
                : "white";

              return (
                <Accordion
                  key={index}
                  sx={{ mb: 1, borderRadius: "5px", overflow: "hidden" }}
                  expanded={isExpanded}
                  onChange={(_, expanded) => {
                    setExpandedDays((prev) => {
                      if (expanded) {
                        return [...prev, index];
                      } else {
                        return prev.filter((i) => i !== index);
                      }
                    });
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}
                    sx={{
                      backgroundColor: summaryBaseBackground,
                      fontWeight: isToday(currentDay) ? "bold" : "normal",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      background:
                        statusColor !== "white"
                          ? isExpanded
                            ? `linear-gradient(135deg, ${statusColor} 0%, ${summaryBaseBackground} ${
                                isSmallScreen ? "15%" : "6%"
                              })` // diagonal when expanded
                            : `linear-gradient(90deg, ${statusColor} 0%, ${summaryBaseBackground} ${
                                isSmallScreen ? "15%" : "6%"
                              })` // horizontal when collapsed
                          : summaryBaseBackground,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      {" "}
                      {hasUnconfirmed && (
                        <Checkbox
                          size="medium"
                          checked={!!checkedDates[currentDayKey]}
                          onChange={(event) => {
                            event.stopPropagation();
                            handleCheckboxChange(
                              currentDayKey,
                              event.target.checked
                            );
                          }}
                          onClick={(event) => event.stopPropagation()}
                          onFocus={(event) => event.stopPropagation()}
                        />
                      )}{" "}
                      <Typography sx={{ flexGrow: 1, textAlign: "center" }}>
                        {format(currentDay, "EEE d MMM", {
                          locale:
                            language && language.startsWith("en") ? enGB : sv,
                        })}
                        {currentHolidayName && (
                          <span>
                            <span className="dash-spacer">—</span>
                            {currentHolidayName}
                          </span>
                        )}
                        {scheduledHours.some(
                          (schedule) => schedule.date === currentDayKey
                        ) && (
                          <span>
                            <span className="dash-spacer">—</span>
                            {(() => {
                              const reportedHoursForDay =
                                getReportedHoursForDate(currentDayKey);
                              const totalReportedHours =
                                roundToTwoDecimals(reportedHoursForDay);

                              const scheduledHoursForDay =
                                scheduledHours.find(
                                  (schedule) => schedule.date === currentDayKey
                                )?.hours || 0;

                              const exceededHours =
                                totalReportedHours > scheduledHoursForDay;

                              return (
                                <>
                                  {`${totalReportedHours} ${t(
                                    "of"
                                  )} ${scheduledHoursForDay} ${t(
                                    "hours"
                                  )}`}{" "}
                                  {exceededHours && (
                                    <Tooltip
                                      title={t(
                                        "timeReport.exceededHours.tooltip"
                                      )}
                                    >
                                      <WarningAmber
                                        sx={{
                                          ml: 1,
                                          fontSize: 16,
                                          color: "#e57373",
                                          verticalAlign: "middle",
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </>
                              );
                            })()}
                          </span>
                        )}
                      </Typography>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    {dayReports.length > 0 ? (
                      <div>
                        {dayDisplayEntries.map((entry, entryIndex) => {
                          if (entry.type === "item") {
                            const item = entry.item;
                            return (
                              <TimeReportItem
                                key={`single-${item.id ?? item.rowNo ?? entryIndex}`}
                                reportItem={item}
                                onEdit={handleEditTimeReport}
                                onDelete={handleDeleteTimeReport}
                                onAdd={handleAddFromItem}
                              />
                            );
                          }

                          const groupStatusColor = getStatusColorForItems(
                            entry.items
                          );

                          return (
                            <Box
                              key={`group-${entry.workOrderNumber}-${entryIndex}`}
                              sx={{ mt: 1 }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: "bold", mb: 0.5, ml: 1 }}
                              >
                                {entry.title}
                              </Typography>
                              <Box
                                sx={{
                                  ml: 2,
                                  border: 1,
                                  borderColor:
                                    groupStatusColor === "white"
                                      ? "divider"
                                      : groupStatusColor,
                                  borderRadius: "5px",
                                  overflow: "hidden",
                                }}
                              >
                                {entry.items.map((item, itemIndex) => (
                                  <TimeReportItem
                                    key={`group-item-${item.id ?? item.rowNo ?? itemIndex}`}
                                    reportItem={item}
                                    onEdit={handleEditTimeReport}
                                    onDelete={handleDeleteTimeReport}
                                    onAdd={handleAddFromItem}
                                    hideWorkOrderPrefix={
                                      entry.type === "workorder-group"
                                    }
                                  />
                                ))}
                              </Box>
                            </Box>
                          );
                        })}
                      </div>
                    ) : (
                      <Typography>
                        {t("timeReport.reportedTimes.noReportedTimes")}{" "}
                        {format(currentDay, "yyyy-MM-dd")}
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
          {/* Status color legend */}
          <div
            style={{
              position: "fixed",
              left: 0,
              right: 0,
              bottom: "130px",
              zIndex: 1300,
              display: "flex",
              justifyContent: "center",
              margin: "0 auto",
              padding: "8px",
              backgroundColor: "white",
              borderRadius: "5px",
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
              maxWidth: 480,
              width: "calc(100% - 32px)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "16px",
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: "#ED9F59",
                  marginRight: "8px",
                  borderRadius: "2px",
                }}
              ></div>
              <Typography variant="body2">
                {t("timeReport.reportedTimes.unconfirmed")}
              </Typography>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "16px",
              }}
            >
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: "#3D60CF",
                  marginRight: "8px",
                  borderRadius: "2px",
                }}
              ></div>
              <Typography variant="body2">
                {t("timeReport.reportedTimes.confirmed")}
              </Typography>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: "#29D55A",
                  marginRight: "8px",
                  borderRadius: "2px",
                }}
              ></div>
              <Typography variant="body2">
                {t("timeReport.reportedTimes.approved")}
              </Typography>
            </div>
          </div>
          {/* Floating action buttons */}
          <div>
            <IconButton
              onClick={handleConfirmClick}
              sx={{
                position: "fixed",
                bottom: "70px",
                right: "80px",
                bgcolor: "#fff",
                borderRadius: "50%",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              }}
              aria-label="Confirm"
              disabled={
                reportedTimes.length === 0 ||
                !reportedTimes.some((item) => item.status === "Unconfirmed") ||
                confirmLoading
              }
            >
              <Check sx={{ fontSize: 32, color: "#29D55A" }} />
            </IconButton>{" "}
            <IconButton
              onClick={handleAddTimeClick}
              sx={{
                position: "fixed",
                bottom: "70px",
                right: "20px",
                bgcolor: "#fff",
                borderRadius: "50%",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              }}
              aria-label="Add time"
            >
              <AddIcon sx={{ fontSize: 32, color: "#333" }} />
            </IconButton>
          </div>
          <ReportTimeModal
            open={isModalOpen}
            onClose={handleCloseModal}
            baseDate={selectedDate}
            editItem={editItem}
            onSave={handleSave}
            reportedTimes={reportedTimes}
            prefillData={prefillData}
            onPrefillConsumed={() => setPrefillData(null)}
          />
          {/* Confirm dates Dialog */}
          <Dialog
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {t("timeReport.confirmDates.title")}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {(() => {
                  const selectedDates = Object.entries(checkedDates)
                    .filter(([_, isChecked]) => isChecked)
                    .map(([date]) => date);

                  const unconfirmedDates = reportedTimes
                    .filter((item) => item.status === "Unconfirmed")
                    .map((item) => item.date);

                  const allUnconfirmedSelected = unconfirmedDates.every(
                    (date) => checkedDates[date]
                  );

                  if (allUnconfirmedSelected) {
                    return t("timeReport.confirmDates.confirmAllWeek", {
                      week: getISOWeek(selectedDate),
                    });
                  } else {
                    return t("timeReport.confirmDates.confirmSelectedDays");
                  }
                })()}
              </DialogContentText>

              {(() => {
                const selectedDates = Object.entries(checkedDates)
                  .filter(([_, isChecked]) => isChecked)
                  .map(([date]) => date);

                // Find any dates with mismatched hours
                const datesWithMismatchedHours = selectedDates.filter(
                  (date) => {
                    const reportedHoursForDay = getReportedHoursForDate(date);

                    const scheduledHoursForDay =
                      scheduledHours.find((schedule) => schedule.date === date)
                        ?.hours || 0;

                    return reportedHoursForDay !== scheduledHoursForDay;
                  }
                );

                const hasAnyMismatchedHours =
                  datesWithMismatchedHours.length > 0;

                return (
                  <>
                    {selectedDates.length > 0 && (
                      <List dense>
                        {selectedDates.sort().map((date) => {
                          const reportedHoursForDay =
                            getReportedHoursForDate(date);

                          const scheduledHoursForDay =
                            scheduledHours.find(
                              (schedule) => schedule.date === date
                            )?.hours || 0;

                          const hoursDoNotMatch =
                            reportedHoursForDay !== scheduledHoursForDay;

                          return (
                            <ListItem key={date}>
                              <ListItemText
                                primary={
                                  <>
                                    {format(new Date(date), "EEEE d MMMM", {
                                      locale:
                                        language && language.startsWith("en")
                                          ? enGB
                                          : sv,
                                    })}
                                    {hoursDoNotMatch && " *"}
                                  </>
                                }
                              />
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                    {hasAnyMismatchedHours && (
                      <DialogContentText
                        sx={{
                          mt: 2,
                          fontSize: "0.875rem",
                          fontStyle: "italic",
                        }}
                      >
                        * {t("timeReport.confirmDates.hoursDoNotMatch")}
                      </DialogContentText>
                    )}
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button onClick={confirmSelectedDates} color="primary" autoFocus>
                {t("timeReport.confirmDates.confirm")}
              </Button>
              <Button
                onClick={() => setConfirmDialogOpen(false)}
                color="secondary"
              >
                {t("timeReport.confirmDates.cancel")}
              </Button>
            </DialogActions>
          </Dialog>
          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
          >
            <DialogTitle id="delete-dialog-title">
              {t("timeReport.deleteConfirmation.title")}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="delete-dialog-description">
                {t("timeReport.deleteConfirmation.message")}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => setDeleteDialogOpen(false)}
                color="secondary"
              >
                {t("timeReport.deleteConfirmation.cancel")}
              </Button>
              <Button
                onClick={confirmDeleteTimeReport}
                color="primary"
                autoFocus
                disabled={confirmLoading}
              >
                {t("timeReport.deleteConfirmation.confirm")}
              </Button>
            </DialogActions>{" "}
          </Dialog>
        </Container>
        <Loading isLoading={confirmLoading || prefillLoading} />
        {/* Edit Time Dialog */}
        <EditTimeDialog
          open={editTimeDialogOpen}
          onClose={() => setEditTimeDialogOpen(false)}
          timeReportItem={timeItemToEdit}
          onSave={getReportedTimes}
        />
        {subMaterialWoNo && (
          <SubMaterialModal
            open={subMaterialOpen}
            onClose={handleCloseSubMaterial}
            onSuccess={handleSubMaterialSuccess}
            mainProductId={null}
            wono={subMaterialWoNo}
            counter={subMaterialCounter}
            setCounter={noopSetCounter}
            isTimeReport={true}
            reportDate={subMaterialDate}
            loading={subMaterialLoading}
          />
        )}
      </ThemeProvider>
      <Footer />
    </div>
  );
};

export default TimeReport;
